import React from 'react';
import { AlertCircle, RefreshCw, Wifi } from 'lucide-react';
import { Button } from './button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './card';

interface ErrorStateProps {
  title?: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  variant?: 'default' | 'network' | 'not-found';
}

export function ErrorState({
  title,
  description,
  actionLabel = 'Повторить',
  onAction,
  variant = 'default'
}: ErrorStateProps) {
  const getIcon = () => {
    switch (variant) {
      case 'network':
        return <Wifi className="w-12 h-12 text-muted-foreground" />;
      case 'not-found':
        return <AlertCircle className="w-12 h-12 text-muted-foreground" />;
      default:
        return <AlertCircle className="w-12 h-12 text-destructive" />;
    }
  };

  const getDefaultTitle = () => {
    switch (variant) {
      case 'network':
        return 'Ошибка соединения';
      case 'not-found':
        return 'Данные не найдены';
      default:
        return 'Что-то пошло не так';
    }
  };

  const getDefaultDescription = () => {
    switch (variant) {
      case 'network':
        return 'Не удалось подключиться к серверу. Проверьте соединение с интернетом.';
      case 'not-found':
        return 'Нет данных для отображения.';
      default:
        return 'Произошла неожиданная ошибка. Повторите попытку.';
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <div className="mx-auto mb-4">
          {getIcon()}
        </div>
        <CardTitle>{title || getDefaultTitle()}</CardTitle>
        <CardDescription>
          {description || getDefaultDescription()}
        </CardDescription>
      </CardHeader>
      {onAction && (
        <CardContent>
          <Button onClick={onAction} className="w-full" variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            {actionLabel}
          </Button>
        </CardContent>
      )}
    </Card>
  );
}

interface QueryErrorProps {
  error: unknown;
  refetch?: () => void;
  className?: string;
}

export function QueryError({ error, refetch, className }: QueryErrorProps) {
  const errorObj = error as any;
  const isNetworkError = errorObj?.status === 0 || !navigator.onLine;
  const isNotFound = errorObj?.status === 404;

  let variant: 'default' | 'network' | 'not-found' = 'default';
  if (isNetworkError) variant = 'network';
  else if (isNotFound) variant = 'not-found';

  return (
    <div className={className}>
      <ErrorState
        title={errorObj?.message || undefined}
        variant={variant}
        onAction={refetch}
        actionLabel={refetch ? 'Повторить' : undefined}
      />
    </div>
  );
}

export function EmptyState({
  title = 'Нет доступных данных',
  description = 'На данный момент нет элементов для отображения.',
  actionLabel,
  onAction,
  icon
}: {
  title?: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  icon?: React.ReactNode;
}) {
  return (
    <div className="text-center p-8">
      <div className="mx-auto mb-4 text-muted-foreground">
        {icon || <AlertCircle className="w-12 h-12" />}
      </div>
      <h3 className="text-lg font-semibold mb-2">{title}</h3>
      <p className="text-muted-foreground mb-4">{description}</p>
      {onAction && actionLabel && (
        <Button onClick={onAction} variant="outline">
          {actionLabel}
        </Button>
      )}
    </div>
  );
}